import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("untamed_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("untamed_refresh", data.refresh_token);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async (email, password, name, phone, marketingConsent = false) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, phone, marketing_consent: !!marketingConsent });
      if (data.access_token) localStorage.setItem("untamed_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("untamed_refresh", data.refresh_token);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("Logout API call failed (session cleared locally):", err); }
    localStorage.removeItem("untamed_token");
    localStorage.removeItem("untamed_refresh");
    setUser(false);
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    try {
      const { data } = await api.put("/auth/profile", profileData);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, updateProfile }),
    [user, loading, login, register, logout, updateProfile]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
